export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly details?: unknown) {
    super(message);
  }
}

/** Built with `VITE_NEXUS_EMBEDDED=1` and `--base /nexus/` to run inside the admin template. */
export const EMBEDDED = import.meta.env.VITE_NEXUS_EMBEDDED === "1";

/** Inside the admin template every Nexus API path is served under `/api/nexus/`. */
export function apiUrl(path: string): string {
  return EMBEDDED && path.startsWith("/api/") ? `/api/nexus/${path.slice(5)}` : path;
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(apiUrl(path), {
    ...init,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });
  const contentType = response.headers.get("content-type") ?? "";
  const body = contentType.includes("application/json") ? await response.json() : await response.text();
  if (!response.ok) {
    const payload = body as { error?: { message?: string; details?: unknown } };
    throw new ApiError(payload.error?.message ?? `Request failed (${response.status})`, response.status, payload.error?.details);
  }
  return body as T;
}

export interface UniversalRecord {
  id: string;
  workspaceId: string;
  objectKey: string;
  title: string;
  status: string;
  ownerId?: string;
  data: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface ObjectDefinition {
  key: string;
  singular: string;
  plural: string;
  description: string;
  icon: string;
  color: string;
  system: boolean;
  titleField: string;
  fields: Array<{ key: string; label: string; type: string; required?: boolean; options?: string[] }>;
  views: string[];
}

export interface Overview {
  workspace: { id: string; name: string; industry: string };
  kpis: { contacts: number; openPipeline: number; wonRevenue: number; tasksDue: number; activeJourneys: number; unreadConversations: number };
  funnel: Array<{ stage: string; count: number; value: number; color: string }>;
  revenue: Array<{ period: string; won: number; pipeline: number }>;
  activities: Array<{ id: string; type: string; subject: string; body?: string; occurredAt: string }>;
  modules: Array<{ key: string; name: string; description: string; version: string }>;
  deliveryMode: "disabled" | "sandbox" | "live";
}

export interface SalonFlowSummary {
  flowId: string;
  flowName: string;
  cases: number;
  validatedCases: number;
  gateProgress: number;
  estLostUsd: number;
  estRecoverableUsd: number;
  recoverablePct: number;
  buildEffort: number;
  gate: "EMPTY" | "BLOCKED" | "WATCH" | "READY";
  topReasons: Array<{ reason: string; count: number }>;
}

export interface SalonFunnelCandidate {
  key: string;
  category: string;
  title: string;
  rationale: string;
  confidence: number;
  expectedImpact: number;
  effort: number;
  score: number;
  evidence: Record<string, unknown>;
  target: Record<string, unknown>;
  flowId: string;
  gate: "BLOCKED" | "WATCH" | "READY";
  estRecoverableUsd: number;
}

export interface SalonRecentCase {
  id: string;
  title: string;
  flowId: string;
  dropReason: string;
  source: string;
  confidence: string;
  validated: boolean;
  estimatedLostUsd: number;
  createdAt: string;
}

export interface SalonFunnelOverview {
  flows: SalonFlowSummary[];
  candidates: SalonFunnelCandidate[];
  recentCases: SalonRecentCase[];
  totals: { cases: number; validatedCases: number; estLostUsd: number; estRecoverableUsd: number; flowsReady: number; flowsBlocked: number; monthlyFeeBasisUsd: number };
  generatedAt: string;
}

export const currency = (value: number, code = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: code, maximumFractionDigits: 0 }).format(value);
export const relative = (value: string) => new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(Math.round((new Date(value).getTime() - Date.now()) / 86_400_000), "day");

export function parseJson<T>(value: unknown, fallback: T): T {
  if (typeof value !== "string") return (value as T) ?? fallback;
  try { return JSON.parse(value) as T; } catch { return fallback; }
}
